/**
 * The rolling log (owner ask 2026-08-25): user progress AND system
 * progress in one reverse-chronological stream — "user did this, yanamas
 * did that." A pure projection over canonical ledgers (activity log,
 * reminder firings, release decisions, prepared days, check-ins); every
 * line traces to a ledger row, which makes this the explainability
 * surface, not just a history widget.
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, RefreshCw, ScrollText } from 'lucide-react'
import { useBranding } from '../../hooks/useBranding'
import { BRAND } from '../../config/brand'

interface FeedEvent {
  at: string
  actor: 'you' | 'yanamas'   // API discriminator, not shown to the person   // API discriminator, not shown to the person
  kind: string
  text: string
  item_type?: string | null
  item_id?: number | null
  item_title?: string | null
}

type Filter = 'all' | 'you' | 'yanamas'

const ITEM_ROUTES: Record<string, string> = {
  task: '/tasks', habit: '/habits', goal: '/goals', dream: '/dreams',
  skill: '/skills',
}

export function UnifiedActivityFeed() {
  const navigate = useNavigate()
  const branding = useBranding()
  const [events, setEvents] = useState<FeedEvent[]>([])
  const [filter, setFilter] = useState<Filter>('all')
  const [loading, setLoading] = useState(true)
  // 2026-08-29: a failed load fell through to "Nothing logged in the last week
  // yet" — an empty state claiming we looked. Failures now say so, with a retry.
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const r = await fetch('/api/activity/v1/feed?days=7', { credentials: 'include' })
      const b = await r.json().catch(() => null)
      if (r.ok && b?.success) {
        setEvents(b.events as FeedEvent[])
      } else {
        setEvents([])
        setError("We couldn't read the log just now.")
      }
    } catch {
      setEvents([])
      setError("We couldn't reach the server to read the log.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const shown = events.filter((e) => filter === 'all' || e.actor === filter)

  const dayLabel = (iso: string) => {
    const d = new Date(iso)
    const today = new Date()
    const yd = new Date(today.getTime() - 86400000)
    if (d.toDateString() === today.toDateString()) return 'Today'
    if (d.toDateString() === yd.toDateString()) return 'Yesterday'
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  }

  let lastDay = ''

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {(['all', 'you', `${BRAND.assistant}`] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 min-h-touch rounded-full text-xs capitalize transition-colors ${
              filter === f
                ? 'bg-brand text-on-brand font-semibold'
                : 'bg-surface border border-soft text-secondary hover:border-brand'
            }`}
          >
            {f === 'all' ? 'Everything' : f === 'you' ? 'you' : branding.assistant}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void load()}
          aria-label="Refresh feed"
          className="ml-auto flex min-h-touch min-w-touch items-center justify-center rounded-lg text-muted hover:text-secondary hover:bg-surface-alt"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {error && !loading && (
        <div className="py-6 text-center text-sm text-warn">
          <AlertTriangle className="mx-auto mb-2 h-5 w-5" />
          <p>{error} Nothing has been lost.</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 inline-flex items-center gap-2 min-h-touch rounded-lg border border-soft px-4 text-secondary hover:text-primary hover:bg-surface-alt"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Try again
          </button>
        </div>
      )}

      {shown.length === 0 && !loading && !error && (
        <p className="py-6 text-center text-sm text-muted">
          <ScrollText className="mx-auto mb-2 h-5 w-5" />
          Nothing logged in the last week yet.
        </p>
      )}

      <div className="space-y-0.5 max-h-[420px] overflow-y-auto pr-1">
        {shown.map((e, i) => {
          const day = dayLabel(e.at)
          const showDay = day !== lastDay
          lastDay = day
          const route = e.item_type && e.item_id != null
            ? ITEM_ROUTES[e.item_type] : e.item_type === 'reminder' ? null : null
          return (
            <div key={`${e.at}-${i}`}>
              {showDay && (
                <div className="sticky top-0 bg-surface py-1 text-app-micro font-bold uppercase tracking-widest text-muted">
                  {day}
                </div>
              )}
              <div
                role={route ? 'link' : undefined}
                onClick={() => route && navigate(`${route}/${e.item_id}`)}
                className={`flex items-baseline gap-2 rounded-lg px-2 py-1.5 ${
                  route ? 'cursor-pointer hover:bg-surface-alt' : ''
                }`}
              >
                <span className="w-12 shrink-0 text-app-micro tabular-nums text-muted">
                  {new Date(e.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                </span>
                <span
                  className={`shrink-0 rounded-full px-1.5 py-0.5 text-app-micro font-semibold ${
                    e.actor === 'you'
                      ? 'bg-ok/15 text-ok'
                      : 'bg-brand-subtle text-brand'
                  }`}
                >
                  {e.actor === 'you' ? 'you' : branding.assistant}
                </span>
                <span className="min-w-0 text-xs text-primary">
                  {e.text}
                  {e.item_title && !e.text.includes(e.item_title) && (
                    // Their own title, not chrome: never quieter than the row
                    // around it (2026-08-29).
                    <span className="font-medium text-primary"> · {e.item_title}</span>
                  )}
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
